import { useLayoutEffect, useRef } from 'react'
import { EMPTY, type Observable, of } from 'rxjs'
import { catchError, filter, switchMap, take } from 'rxjs/operators'
import { withAbortSignal } from '@/utils/rx-util'
import { useEffectEvent } from '@react-aria/utils'

/**
 * Options for {@link useUrlSyncedViewModelState}.
 */
export interface UrlSyncedViewModelStateOptions<TVm, TUrl = TVm> {
  /**
   * The value currently encoded in the URL, or `null` when the URL doesn't
   * carry one. Should be stable between renders.
   */
  urlValue: TUrl | null

  /**
   * Stream of the view model's current value. It should emit the current value
   * on subscribe and only emit again when the value actually changes.
   * Should be stable between renders.
   */
  vmValue$: Observable<TVm>

  /**
   * Navigates so the URL reflects the given view model value. Call your own
   * `navigate` however you like, or do nothing to leave the URL untouched.
   *
   * Return the navigation promise so a newer value can supersede one that's
   * still in flight. When that happens the `signal` aborts, so you can bail out
   * of any async work you kicked off.
   */
  syncToUrl: (value: TVm, signal: AbortSignal) => Promise<unknown> | void

  /**
   * Applies a URL value to the view model. Gets `null` when the URL doesn't
   * carry a value, so you can fall back to a default.
   */
  applyToVm: (urlValue: TUrl | null) => void

  /**
   * Optionally holds off syncing until this emits `true`. Like {@link vmValue$},
   * it should emit its current value on subscribe and stay stable between
   * renders.
   */
  ready$?: Observable<boolean>

  /**
   * Called once, right after the URL is first applied to the view model. Useful
   * for letting the view model know the initial deep link has been restored.
   */
  onInitialSync?: () => void
}

/**
 * Keeps a piece of view-model-owned state, like the active tab or selected
 * item, in sync with the URL.
 *
 * When the URL changes, including on the first load or from a deep link, the
 * value gets handed to `applyToVm` so the view model can pick it up. Going the
 * other way, a change to the view model's value calls `syncToUrl` to update the
 * URL.
 *
 * We always apply the URL to the view model before mirroring anything back out,
 * so a deep-linked value won't get clobbered by the view model's default on the
 * first paint.
 *
 * The callbacks can be passed inline; only the streams need a stable identity.
 */
export function useUrlSyncedViewModelState<TVm, TUrl = TVm>({
  urlValue,
  vmValue$,
  syncToUrl,
  applyToVm,
  ready$,
  onInitialSync,
}: UrlSyncedViewModelStateOptions<TVm, TUrl>): void {
  // Wrap the callbacks so they stay stable but always see the latest props.
  // That's what lets callers pass them inline without memoizing.
  const syncToUrlEvent = useEffectEvent((value: TVm, signal: AbortSignal) =>
    syncToUrl(value, signal),
  )
  const applyToVmEvent = useEffectEvent((value: TUrl | null) =>
    applyToVm(value),
  )
  const onInitialSyncEvent = useEffectEvent(() => onInitialSync?.())

  const hasSyncedRef = useRef(false)

  // Apply the URL to the view model. If a `ready$` was given, we wait for it
  // to emit before applying.
  useLayoutEffect(() => {
    const applyFromUrl = () => {
      applyToVmEvent(urlValue)
      if (!hasSyncedRef.current) {
        hasSyncedRef.current = true
        onInitialSyncEvent()
      }
    }

    if (!ready$) {
      applyFromUrl()
      return
    }

    const subscription = ready$
      .pipe(filter(Boolean), take(1))
      .subscribe(applyFromUrl)
    return () => subscription.unsubscribe()
  }, [urlValue, ready$])

  // Sync the view model's value back out to the URL. This runs after the
  // effect above, so the URL has already been applied before we start.
  useLayoutEffect(() => {
    const whenReady$ = ready$ ? ready$.pipe(filter(Boolean), take(1)) : of(true)
    const subscription = whenReady$
      .pipe(
        switchMap(() => vmValue$),
        switchMap((value) =>
          withAbortSignal((signal) =>
            Promise.resolve(syncToUrlEvent(value, signal)),
          ).pipe(
            // A failed or aborted sync shouldn't tear the subscription down.
            // Swallow it here on the inner stream so we keep mirroring later
            // values instead of stopping for good.
            catchError(() => EMPTY),
          ),
        ),
      )
      .subscribe()
    return () => subscription.unsubscribe()
  }, [vmValue$, ready$])
}
